import { createApi } from '@reduxjs/toolkit/query/react';

import { normalizeEnterprise } from '@/slices/enterprises/enterprise.normalizer';
import {
  Enterprise,
  EnterpriseManager,
  EnterpriseManagersPayload,
  EnterprisePayload,
  EnterprisesPayload,
} from '@/slices/enterprises/enterprise.types';

import { symfonyBaseQuery } from '@/services/SymfonyClient';

const enterpriseApi = createApi({
  baseQuery: symfonyBaseQuery({
    baseUrl: '/api',
  }),
  reducerPath: 'enterprise',
  tagTypes: ['Enterprise', 'EnterpriseManagers'],
  endpoints: (build) => ({
    getEnterprise: build.query<
      Enterprise,
      {
        enterpriseId: string;
      }
    >({
      query: ({ enterpriseId }) => ({
        url: `/enterprises/${enterpriseId}`,
        method: 'GET',
      }),
      transformResponse: (response: EnterprisePayload) =>
        normalizeEnterprise(response),
    }),
    getManagersOfEnterprise: build.query<
      EnterpriseManager[],
      {
        enterpriseId: string;
      }
    >({
      query: ({ enterpriseId }) => ({
        url: `/enterprises/${enterpriseId}/managers`,
        method: 'GET',
      }),
      transformResponse: (response: EnterpriseManagersPayload) =>
        response.managers ?? [],
      providesTags: (_result, _error, { enterpriseId }) => [
        { type: 'EnterpriseManagers', id: enterpriseId },
      ],
    }),
    getEnterprises: build.query<Enterprise[], void>({
      query: () => ({
        url: '/admin/enterprises',
        method: 'GET',
      }),
      transformResponse: (response: EnterprisesPayload) =>
        (response.enterprises ?? []).map(normalizeEnterprise),
      providesTags: (result) => [
        { type: 'Enterprise', id: 'LIST' },
        ...(result ?? []).map(({ id }) => ({
          type: 'Enterprise' as const,
          id,
        })),
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          for (const enterprise of data) {
            dispatch(
              enterpriseApi.util.upsertQueryData(
                'getEnterprise',
                { enterpriseId: enterprise.id },
                enterprise,
              ),
            );
          }
        } catch {
          // Ignore cache sync when list request fails.
        }
      },
    }),
  }),
});

export default enterpriseApi;
